import React, { useCallback, useEffect, useRef, useState } from "react";
import SignaturePadLib from "signature_pad";
import "@fontsource/dancing-script/400.css";
import "./SignaturePad.css";

// A Signature question: "Draw" (mouse, touch, pen) or "Type" (the name the
// person types, drawn in a cursive font). The value is "" (not signed) or
// { mode: "draw" | "type", dataUrl: "data:image/png;base64,...", name? }.
// The page turns dataUrl into a PNG file on submit; the server keeps only
// the file, the mode and its own time.

export const MIN_INK_PX = 60;
const HEIGHT = 160;
const TYPE_FONT_FAMILY = '"Dancing Script", cursive';

// Total length of the drawn strokes, in CSS pixels.
export function inkLength(strokes) {
    let total = 0;
    (strokes || []).forEach(stroke => {
        const pts = stroke.points || [];
        for (let i = 1; i < pts.length; i++) total += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    });
    return total;
}

const pixelRatio = () => Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);

export const DEFAULT_INK = "#111827";

// The form's answer colour as ink, or the default when it isn't a colour the
// browser understands. (Some forms store "#rgb(55, 65, 81)"; a canvas keeps
// its previous fill for an invalid colour, which made drawn strokes
// invisible.)
export function inkColor(color) {
    if (typeof color !== "string" || !color.trim()) return DEFAULT_INK;
    try {
        if (window.CSS && typeof window.CSS.supports === "function") {
            return window.CSS.supports("color", color) ? color : DEFAULT_INK;
        }
    } catch (e) { /* fall through */ }
    const probe = document.createElement("canvas").getContext("2d");
    if (!probe) return DEFAULT_INK;
    probe.fillStyle = "#010203";
    probe.fillStyle = color;
    return probe.fillStyle === "#010203" && color.toLowerCase() !== "#010203" ? DEFAULT_INK : color;
}

// tryOut: the builder and the template preview. Works like the live form
// (draw, type, clear) but keeps the signature to itself; nothing is saved.
export default function SignaturePad({ value, onChange, disabled = false, tryOut = false, color: formColor = DEFAULT_INK }) {
    const color = inkColor(formColor);
    const [ownValue, setOwnValue] = useState("");
    const shown = tryOut ? ownValue : value;
    const current = shown && typeof shown === "object" && shown.dataUrl ? shown : null;
    const [mode, setMode] = useState(current ? current.mode : "draw");
    const [name, setName] = useState(current && current.mode === "type" ? current.name || "" : "");
    const [tooShort, setTooShort] = useState(false);
    const drawRef = useRef(null);
    const typeRef = useRef(null);
    const padRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = tryOut ? setOwnValue : (onChange || (() => { }));

    // Canvas pixels match its CSS size (sharp lines on any screen).
    const sizeCanvas = (canvas) => {
        const ratio = pixelRatio();
        const width = canvas.offsetWidth || 480;
        canvas.width = width * ratio;
        canvas.height = HEIGHT * ratio;
        canvas.getContext("2d").setTransform(ratio, 0, 0, ratio, 0, 0);
        return { ratio, width };
    };

    // Draw tab: signature_pad on the canvas.
    useEffect(() => {
        if (disabled || mode !== "draw" || !drawRef.current) return undefined;
        const canvas = drawRef.current;
        const { ratio, width } = sizeCanvas(canvas);
        const pad = new SignaturePadLib(canvas, { penColor: color, backgroundColor: "rgba(0,0,0,0)", minWidth: 0.8, maxWidth: 2.6 });
        padRef.current = pad;
        if (current && current.mode === "draw") pad.fromDataURL(current.dataUrl, { ratio, width, height: HEIGHT }).catch(() => { });
        const onEnd = () => {
            if (inkLength(pad.toData()) < MIN_INK_PX) {
                setTooShort(true);
                onChangeRef.current("");
            } else {
                setTooShort(false);
                onChangeRef.current({ mode: "draw", dataUrl: pad.toDataURL("image/png") });
            }
        };
        pad.addEventListener("endStroke", onEnd);
        return () => {
            pad.removeEventListener("endStroke", onEnd);
            pad.off();
            padRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, mode, color]);

    // Type tab: draw the typed name in the cursive font once it has loaded.
    // Each keystroke starts a render that waits for the font; only the
    // newest may draw (older ones finishing late used to stack up the name).
    const renderSeq = useRef(0);
    const renderTyped = useCallback(async (text) => {
        const seq = ++renderSeq.current;
        const trimmed = text.trim();
        let size = 52;
        if (trimmed) {
            try { await document.fonts.load(`${size}px ${TYPE_FONT_FAMILY}`); } catch (e) { /* falls back to cursive */ }
        }
        const canvas = typeRef.current;
        if (seq !== renderSeq.current || !canvas) return;
        const { width } = sizeCanvas(canvas); // also clears the canvas
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, width, HEIGHT);
        if (!trimmed) {
            onChangeRef.current("");
            return;
        }
        ctx.font = `${size}px ${TYPE_FONT_FAMILY}`;
        while (size > 18 && ctx.measureText(trimmed).width > width - 32) {
            size -= 2;
            ctx.font = `${size}px ${TYPE_FONT_FAMILY}`;
        }
        ctx.fillStyle = color;
        ctx.textBaseline = "middle";
        ctx.textAlign = "center";
        ctx.fillText(trimmed, width / 2, HEIGHT / 2);
        onChangeRef.current({ mode: "type", name: text, dataUrl: canvas.toDataURL("image/png") });
    }, [color]);

    useEffect(() => {
        if (!disabled && mode === "type") renderTyped(name);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const switchTo = (next) => {
        if (next === mode || disabled) return;
        setMode(next);
        setTooShort(false);
        setName("");
        onChangeRef.current("");
    };

    const clear = () => {
        if (disabled) return;
        setTooShort(false);
        if (mode === "draw") {
            if (padRef.current) padRef.current.clear();
            onChangeRef.current("");
        } else {
            setName("");
            renderTyped("");
        }
    };

    return (
        <div className={`signature-pad${disabled ? " signature-pad-disabled" : ""}`}>
            <div className="signature-tabs" role="tablist">
                {["draw", "type"].map(m => (
                    <button
                        key={m}
                        type="button"
                        role="tab"
                        aria-selected={mode === m}
                        className={`signature-tab${mode === m ? " active" : ""}`}
                        onClick={() => switchTo(m)}
                        disabled={disabled}
                    >
                        {m === "draw" ? "Draw" : "Type"}
                    </button>
                ))}
            </div>

            {mode === "type" && !disabled && (
                <input
                    type="text"
                    className="form-control signature-name"
                    placeholder="Type your full name"
                    aria-label="Type your full name"
                    value={name}
                    maxLength={80}
                    onChange={(e) => { setName(e.target.value); renderTyped(e.target.value); }}
                />
            )}

            <div className="signature-canvas-wrap">
                <canvas
                    ref={mode === "draw" ? drawRef : typeRef}
                    key={mode}
                    className="signature-canvas"
                    style={{ height: HEIGHT }}
                    aria-label={mode === "draw" ? "Sign here" : "Your typed signature"}
                />
                {disabled && <span className="signature-placeholder">Signature</span>}
                {!disabled && mode === "draw" && !current && !tooShort && <span className="signature-placeholder">Sign here</span>}
            </div>

            <div className="signature-foot">
                {tooShort
                    ? <span className="signature-hint">Please sign a little more clearly.</span>
                    : <span className="signature-hint signature-hint-muted">{mode === "draw" ? "Draw with your mouse, finger or pen." : "Your name is shown in a signature style."}</span>}
                <button type="button" className="signature-clear" onClick={clear} disabled={disabled}>Clear</button>
            </div>
        </div>
    );
}

// A data: URL as a PNG Blob (for the upload).
export function dataUrlToBlob(dataUrl) {
    const [head, b64] = String(dataUrl).split(",");
    const mime = (/data:([^;]+)/.exec(head) || [])[1] || "image/png";
    const bin = atob(b64 || "");
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

// "Signed (drawn)" for a value on the page (the server adds the time).
export function signatureLabel(value) {
    if (!value || typeof value !== "object") return "";
    return `Signed (${value.mode === "type" ? "typed" : "drawn"})`;
}
